import { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogContentText,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import { addRelationshipSkills } from '../../../services/advisor_services';
import ACSnackbar from '../../../components/ACSnackbar';
import LoadingLogoSkeleton from '../../../components/SkeletonLoading/loadingLogoSkeleton';
import eventBus from '../../../eventBus';

const useStyles = () => ({
  dialogContent: {
    padding: { xxs: '20px', xs: '8px' },
    overflow: 'visible',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  dialogTextCompanyName: {
    textAlign: 'center',
    width: { xxs: '100vw', xs: '100%' },
    padding: { xxs: '0px 8px', xs: '0px' },
  },
  tagsBox: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px 0px',
    margin: '0px',
    width: { xxs: '75%', xs: '100%' },
  },
  tagButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '12px',
    borderRadius: '16px',
    margin: { xxs: '8px 0px', xs: '3px 10px' },
    padding: '2px 12px',
  },
});

const tags = [
  'Product Feedback',
  'Marketing',
  'Operations',
  'Technology',
  'Finance',
  'Sales',
  'Human Resources',
  'Legal',
  'Fundraising',
  'Introductions',
  'International Expansion',
  'Growth Strategies',
  'Customer Experience',
  'New Ideas',
  'Management & Leadership',
];

export default function Step3({
  setActiveStep,
  companyBoardData,
  setBlockClosePopup,
  handleClose,
}) {
  const { accountDetails } = useAccount();
  const [skills, setSkills] = useState([]);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [companyIsP2P, setcompanyIsP2P] = useState();
  const classes = useStyles();

  async function handleSkillsUpdate() {
    if (!skills.length) {
      return;
    }
    await addRelationshipSkills({
      skills: skills,
      advisorId: accountDetails.id,
      companyId: companyBoardData.id,
    });
  }

  async function handleNext() {
    try {
      setLoading(true);
      setBlockClosePopup(true);
      await handleSkillsUpdate();
      setActiveStep((prev) => prev + 1);
      eventBus.dispatch('replayAnimation');
    } catch (error) {
      console.error('Error handling next step:', error);
      setIsSnackbarVisible(true);
    } finally {
      setLoading(false);
      setBlockClosePopup(false);
    }
  }

  useEffect(async () => {
    let isMounted = true;

    if (isMounted && companyBoardData) {
      setcompanyIsP2P(companyBoardData.boardType == "P2P");
    }

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <DialogContent sx={classes.dialogContent}>
      <DialogContentText sx={classes.dialogTextCompanyName}>
        <Typography
          variant="caption"
          sx={{
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '-0.43px',
            textTransform: 'none',
            fontWeight: 700,
            color: '#232B35',
            verticalAlign: 'middle',
            marginTop: '8px',
          }}
        >
          { companyIsP2P ? 
            `${accountDetails.givenName}, what expertise are you most excited to share or learn about?` :
            `${accountDetails.givenName}, in general, what are you most excited about helping this advisory board with?`}
        </Typography>
      </DialogContentText>

      <Box sx={classes.tagsBox}>
        {tags.map((tag, index) => (
          <Button
            key={index}
            variant="outlined"
            color="primary"
            onClick={() => {
              setSkills((prev) =>
                prev.includes(tag)
                  ? prev.filter((skill) => skill !== tag)
                  : [...prev, tag]
              );
            }}
            sx={{
              ...classes.tagButton,
              backgroundColor: skills.includes(tag) ? '#2B71F6' : '#F5F7FA',
              color: skills.includes(tag) ? '#FFFFFF' : '#232B35',
              borderColor: skills.includes(tag) ? '#2B71F6' : '#F5F7FA',
              '&:hover': {
                backgroundColor: skills.includes(tag) ? '#2B71F6' : '#F5F7FA',
                color: skills.includes(tag) ? '#FFFFFF' : '#232B35',
                borderColor: skills.includes(tag) ? '#2B71F6' : '#F5F7FA',
              },
            }}
          >
            {tag}
          </Button>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '0px',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={ companyIsP2P ? handleClose : handleNext}
          disabled={loading}
          className="getStartedButton"
          sx={{
            paddingTop: '15px',
            paddingBottom: '15px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '15px',
            width: '180px',
            height: '44px',
            borderRadius: '30px',
          }}
        >
          {loading ? <LoadingLogoSkeleton /> : companyIsP2P ? "Finish" : "Next"}
        </Button>
      </Box>
      <ACSnackbar
        onClose={() => setIsSnackbarVisible(false)}
        open={isSnackbarVisible}
        severity="error"
        text="Something went wrong"
      />
    </DialogContent>
  );
}
